/** @file Weights panel: brush mode, paint bone, radius, strength, hardness, replace value, auto-normalise. */
import type { CatalogKey, Translate } from '@pose2d/i18n/types';
import { brushStore, type BrushState } from '../../tools/paintWeights/brushStore';
import type { BrushMode } from '../../tools/paintWeights/paintDab';
import { Checkbox } from '../../ui-kit/Checkbox';
import { NumberField } from '../../ui-kit/NumberField';
import { Select } from '../../ui-kit/Select';
import type { WeightsView } from './weightsView';

const MODES: readonly { mode: BrushMode; key: CatalogKey }[] = [
  { mode: 'add', key: 'tools.paintWeights.add' },
  { mode: 'subtract', key: 'tools.paintWeights.subtract' },
  { mode: 'replace', key: 'tools.paintWeights.replace' },
  { mode: 'smooth', key: 'tools.paintWeights.smooth' },
];
const set = (patch: Partial<BrushState>): void => { brushStore.setState(patch); };
const unitField = { min: 0, max: 1, step: 0.01, precision: 2 };

/** Settings of the paintWeights brush (brushStore); the radius is in screen pixels. */
export function BrushSection({ view, t, brush }: { view: WeightsView; t: Translate; brush: BrushState }) {
  const pickMode = (value: string): void => {
    const picked = MODES.find((m) => m.mode === value);
    if (picked !== undefined) set({ mode: picked.mode });
  };
  return (
    <section className="weights-section" aria-label={t('tools.paintWeights.title')}>
      <Select label={t('tools.paintWeights.mode')} value={brush.mode} options={MODES.map((m) => ({ value: m.mode, label: t(m.key) }))} onChange={pickMode} />
      <Select label={t('tools.paintWeights.bone')} value={view.paintBone} options={view.choices.map((c) => ({ value: c.id, label: c.name }))} onChange={(bone) => { set({ bone }); }} />
      <NumberField label={t('tools.paintWeights.radius')} value={brush.radiusPx} min={4} max={400} step={1} precision={0} onCommit={(radiusPx) => { set({ radiusPx }); }} />
      <NumberField label={t('tools.paintWeights.strength')} value={brush.strength} {...unitField} onCommit={(strength) => { set({ strength }); }} />
      <NumberField label={t('tools.paintWeights.hardness')} value={brush.hardness} {...unitField} onCommit={(hardness) => { set({ hardness }); }} />
      <NumberField label={t('tools.paintWeights.value')} value={brush.value} {...unitField} disabled={brush.mode !== 'replace'} onCommit={(value) => { set({ value }); }} />
      <Checkbox label={t('tools.paintWeights.autoNormalize')} checked={brush.autoNormalize} onChange={(autoNormalize) => { set({ autoNormalize }); }} />
    </section>
  );
}
